import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/data/categories';
import SeoArticleJsonLd from '@/components/base/SeoArticleJsonLd';

export default function ArticleMistakes() {
  useEffect(() => {
    document.title = 'Najczęstsze błędy Kakobuy – Czego unikać przy zakupach | Kakobuy Polska Wiki';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <SeoArticleJsonLd
        headline="Najczęstsze błędy przy korzystaniu z Kakobuy – Czego unikać?"
        description="Poznaj najczęstsze błędy popełniane przez początkujących użytkowników Kakobuy. Dowiedz się, jak ich unikać i oszczędzić sobie stresu, czasu i pieniędzy."
        url="/artykul/najczestsze-bledy-kakobuy"
        datePublished="2026-07-17"
        dateModified="2026-07-20"
        imageUrl="https://readdy.ai/api/search-image?query=Thoughtful%20concept%20showing%20a%20hand%20pausing%20over%20a%20laptop%20keyboard%20on%20warm%20cream%20desk%2C%20soft%20natural%20light%2C%20modern%20minimalist%20workspace%2C%20gentle%20amber%20and%20sage%20tones%2C%20reflective%20mood%2C%20editorial%20photography%20with%20subtle%20depth%20of%20field%2C%20calm%20and%20educational%20atmosphere&width=1200&height=514&seq=article-mistakes-v2&orientation=landscape"
      />
      <header className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line"></i>
            Strona Główna
          </Link>
          <span className="text-foreground-400">/</span>
          <span className="text-sm text-foreground-500">Porady</span>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100/80 text-accent-700 text-xs font-semibold mb-4">Porady</span>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
            Najczęstsze błędy przy korzystaniu z Kakobuy – Czego unikać?
          </h1>
          <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
            Poznaj najczęstsze błędy popełniane przez początkujących użytkowników Kakobuy. Dowiedz się, jak ich unikać i oszczędzić sobie stresu, czasu i pieniędzy.
          </p>
          <div className="aspect-[21/9] rounded-2xl overflow-hidden mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=Thoughtful%20concept%20showing%20a%20hand%20pausing%20over%20a%20laptop%20keyboard%20on%20warm%20cream%20desk%2C%20soft%20natural%20light%2C%20modern%20minimalist%20workspace%2C%20gentle%20amber%20and%20sage%20tones%2C%20reflective%20mood%2C%20editorial%20photography%20with%20subtle%20depth%20of%20field%2C%20calm%20and%20educational%20atmosphere&width=1200&height=514&seq=article-mistakes-v2&orientation=landscape"
              alt="Najczęstsze błędy Kakobuy"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="prose-custom space-y-8 text-foreground-700 text-sm md:text-base leading-relaxed">
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">1. Pomijanie zdjęć QC</h2>
            <p>To <strong>najczęstszy i najpoważniejszy błąd</strong>. Niektórzy użytkownicy od razu wysyłają produkty do Polski bez sprawdzenia zdjęć QC. Zdjęcia QC są darmowe i pozwalają wykryć wady, niezgodność z opisem czy uszkodzenia. Zawsze sprawdzaj zdjęcia QC przed zatwierdzeniem wysyłki międzynarodowej.</p>
          </section>
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">2. Wybór najtańszej wysyłki</h2>
            <p>Linie ekonomiczne jak EUB są tanie, ale <strong>ryzyko dodatkowych opłat celnych</strong> jest wysokie. Dopłata 50-80 PLN za linię tax-free to inwestycja w spokój — unikasz stresu związanego z cłem, a paczka dociera szybciej i bezpieczniej.</p>
          </section>
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">3. Nieczytanie opinii o sprzedawcach</h2>
            <p>Kakobuy Spreadsheet zawiera sprawdzone linki, ale przy samodzielnym szukaniu <strong>nie pomijaj ocen sprzedawców</strong>. Sprawdzaj procent pozytywnych opinii, datę rejestracji sklepu i zdjęcia od innych kupujących na Taobao czy Weidian.</p>
          </section>
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">4. Zamawianie bez sprawdzenia rozmiaru</h2>
            <p><strong>Chińskie rozmiary różnią się od europejskich</strong>. Zawsze sprawdzaj tabele rozmiarów (size chart) na stronie produktu. W przypadku ubrań zamawiaj minimum o 1-2 rozmiary większe niż nosisz w Polsce. W razie wątpliwości poproś Kakobuy o zmierzenie produktu na zdjęciach QC.</p>
          </section>
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">5. Nieubezpieczanie drogich zamówień</h2>
            <p>Dla zamówień powyżej 500 PLN <strong>ubezpieczenie to must-have</strong>. Koszt to zazwyczaj 3-5% wartości, ale w przypadku zagubienia lub uszkodzenia paczki otrzymujesz pełny zwrot. To niewielka cena za spokój ducha.</p>
          </section>
        </div>

        <div className="mt-12 p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200/70 text-center">
          <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">Unikaj błędów — kupuj mądrze</h3>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
              Kakobuy Spreadsheet
            </a>
            <Link to="/" className="w-full sm:w-auto px-6 py-3 bg-background-50 text-foreground-700 font-semibold text-sm rounded-full hover:bg-background-200 transition-all cursor-pointer whitespace-nowrap border border-background-200">
              Strona Główna
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}